import {
  KAANNETYN_VEROVELVOLLISUUDEN_LAJIT,
  Lasku,
  LaskuBase,
  LaskunAlv,
  LaskunAsiakas,
  LaskunLahetystapa,
  LaskunLuonne,
  LaskunTyypit,
  LaskunTyyppi,
  LaskunTyyppiavain,
  LaskunumeroTyyppi,
} from '@lemon/core-firebase/model/lasku'

import type { LaskunRiviLomake } from './lasku-rivit.component'

export const LAHETYKSEN_VALILEHDET = { SAHKOPOSTI: 0, VERKKOLASKU: 1, ITSE: 2 } as const

export interface RekisterinMuutoksenTiedot {
  nimi: string
  onUusiTuote: boolean
  hinta: number
  yksikko: string
  alv: LaskunAlv
  vanhaHinta?: number
  vanhaYksikko?: string
  vanhaAlvTunniste?: string
}

export interface Kaannos {
  avain: string
  parametrit?: Record<string, unknown>
}

export interface SivunOtsikonTiedot {
  luonnettaVoiValita: boolean
  onkoTallennettu: boolean
  luonne: LaskunLuonne
}

/** Verkkolaskuosoite ratkaisee lähetystavan vain, jos asiakkaalle ei ole valittu oletustapaa. */
export function annaOletuslahetysvalilehti(asiakas: Pick<LaskunAsiakas, 'sahkoinenosoite' | 'oletusLahetystapa' | 'viimeisinLaskuLahetetty'>): number {
  const asiakkaallaOnVerkkolaskuosoite = !!asiakas.sahkoinenosoite?.sahkoinenOsoite
  if (asiakkaallaOnVerkkolaskuosoite && !asiakas.oletusLahetystapa) {
    return LAHETYKSEN_VALILEHDET.VERKKOLASKU
  }
  switch (asiakas.oletusLahetystapa ?? asiakas.viimeisinLaskuLahetetty) {
    case LaskunLahetystapa.SAHKOINEN:
      return LAHETYKSEN_VALILEHDET.VERKKOLASKU
    case LaskunLahetystapa.ITSE:
      return LAHETYKSEN_VALILEHDET.ITSE
    default:
      return LAHETYKSEN_VALILEHDET.SAHKOPOSTI
  }
}

export function annaRivinRekisterinMuutos(
  rivi: Pick<LaskunRiviLomake, 'nimi' | 'tallennetaanRekisteriin' | 'tuote' | 'ahinta' | 'yksikko' | 'alv'>,
  laskunTyyppi: string,
): RekisterinMuutoksenTiedot | null {
  const nimi = rivi.nimi?.trim()
  if (!nimi || !rivi.tallennetaanRekisteriin) {
    return null
  }
  const tuote = rivi.tuote
  const muutos: RekisterinMuutoksenTiedot = {
    nimi,
    onUusiTuote: !tuote?.$key,
    hinta: rivi.ahinta,
    yksikko: rivi.yksikko,
    alv: rivi.alv,
  }
  if (muutos.onUusiTuote) {
    return muutos
  }
  const hintaMuuttui = Math.abs((muutos.hinta ?? 0) - (tuote.hinta ?? 0)) > 0.005
  if (hintaMuuttui) {
    muutos.vanhaHinta = tuote.hinta ?? 0
  }
  const yksikkoMuuttui = !!tuote.yksikko && tuote.yksikko !== muutos.yksikko
  if (yksikkoMuuttui) {
    muutos.vanhaYksikko = tuote.yksikko
  }
  const vanhaAlvTunniste = tuote.alv?.[laskunTyyppi]
  const alvMuuttui = !!vanhaAlvTunniste && vanhaAlvTunniste !== muutos.alv?.tunniste
  if (alvMuuttui) {
    muutos.vanhaAlvTunniste = vanhaAlvTunniste
  }
  const jokinMuuttui = hintaMuuttui || yksikkoMuuttui || alvMuuttui
  return jokinMuuttui ? muutos : null
}

/** Tiivistelmän osat, jotka liitetään otsikkoon `lasku.rekisteri.lahetysvaiheessa` pilkuin erotettuina. */
export function annaRekisterinMuutostenOtsikonOsat(muutokset: Pick<RekisterinMuutoksenTiedot, 'onUusiTuote' | 'vanhaYksikko' | 'vanhaAlvTunniste'>[]): Kaannos[] {
  const uusia = muutokset.filter(muutos => muutos.onUusiTuote).length
  const paivitettavat = muutokset.filter(muutos => !muutos.onUusiTuote)
  const osat: Kaannos[] = []
  if (uusia === 1) {
    osat.push({ avain: 'lasku.rekisteri.uusi-tuote-tallennetaan' })
  } else if (uusia > 1) {
    osat.push({ avain: 'lasku.rekisteri.uutta-tuotetta-tallennetaan', parametrit: { maara: uusia } })
  }
  if (paivitettavat.length) {
    const vainHintoja = paivitettavat.every(muutos => muutos.vanhaYksikko === undefined && muutos.vanhaAlvTunniste === undefined)
    const avain = vainHintoja ? 'lasku.rekisteri.tuotteen-hinta-paivitetaan' : 'lasku.rekisteri.tuotteen-tiedot-paivitetaan'
    osat.push({ avain, parametrit: { maara: paivitettavat.length } })
  }
  return osat
}

/**
 * Myynnin laji, jolla lasku avataan. Laskuttajalta poistunut laji vaihtuu kotimaan myynniksi, jos se oli käännetty
 * verovelvollisuus, mutta hyvitys ja korjaus säilyttävät alkuperäisen laskun lajin.
 */
export function annaLaskulleKaytettavaTyyppi(tyyppiavain: LaskunTyyppiavain, onkoHyvitysTaiKorjaus: boolean, sallitut: LaskunTyyppi[]): LaskunTyyppi | null {
  const sallittuTyyppi = sallitut.find(tyyppi => tyyppi.avain === tyyppiavain)
  if (sallittuTyyppi) {
    return sallittuTyyppi
  }
  if (onkoHyvitysTaiKorjaus) {
    return LaskunTyypit.annaLaskunTyyppi(tyyppiavain)
  }
  const onkoKaannettyVerovelvollisuus = KAANNETYN_VEROVELVOLLISUUDEN_LAJIT.includes(tyyppiavain)
  if (onkoKaannettyVerovelvollisuus) {
    return LaskunTyypit.TAVALLINEN
  }
  return null
}

export function annaLaskunLuonne(juurilasku: Pick<Lasku, 'kateislasku' | 'ennakkolasku'>): LaskunLuonne {
  if (juurilasku.kateislasku) {
    return 'kateislasku'
  }
  if (juurilasku.ennakkolasku) {
    return 'ennakkolasku'
  }
  return 'lasku'
}

/** Käteis- tai ennakkolaskuksi voi valita vain tavallisen alkuperäisen laskun, ei hyvitystä, korjausta tai muistutusta. */
export function voikoLaskunLuonteenValita(juurilasku: Pick<Lasku, 'avain'>, kasiteltava: Pick<LaskuBase, 'avain' | 'nrotyyppi'>): boolean {
  return kasiteltava.nrotyyppi === LaskunumeroTyyppi.TAVALLINEN && juurilasku.avain === kasiteltava.avain
}

export function annaSivunOtsikonAvain(tiedot: SivunOtsikonTiedot): string {
  if (!tiedot.luonnettaVoiValita) {
    return 'lasku.title'
  }
  if (tiedot.onkoTallennettu) {
    return 'lasku.luonnos-otsikko'
  }
  if (tiedot.luonne === 'kateislasku') {
    return 'lasku.uusi-kateislasku'
  }
  if (tiedot.luonne === 'ennakkolasku') {
    return 'lasku.uusi-ennakkolasku'
  }
  return 'lasku.uusi-lasku'
}
